import type { ObjectShape } from 'yup';
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PHONE_NUMBER_REGEX } from '@pflegenavi/shared/constants';

/**
 * Validation schema for Stripe service provider account connection.
 * This schema is used for the Stripe onboarding flow which does not require
 * legal person type or company number fields.
 */
export const useStripeConnectValidationSchema = (): ObjectShape => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      firstName: Yup.string()
        .min(2, t('service-provider-app.connect-account.form.valid.first-name'))
        .max(
          255,
          t('service-provider-app.connect-account.form.valid.first-name')
        )
        .required(
          t('service-provider-app.connect-account.form.required.first-name')
        ),
      lastName: Yup.string()
        .min(2, t('service-provider-app.connect-account.form.valid.last-name'))
        .max(
          255,
          t('service-provider-app.connect-account.form.valid.last-name')
        )
        .required(
          t('service-provider-app.connect-account.form.required.last-name')
        ),
      companyName: Yup.string()
        .min(
          2,
          t('service-provider-app.connect-account.form.valid.company-name')
        )
        .max(
          255,
          t('service-provider-app.connect-account.form.valid.company-name')
        )
        .required(
          t('service-provider-app.connect-account.form.required.company-name')
        ),
      address: Yup.string()
        .min(4, t('service-provider-app.connect-account.form.valid.address'))
        .max(255, t('service-provider-app.connect-account.form.valid.address'))
        .required(
          t('service-provider-app.connect-account.form.required.address')
        ),
      zipCode: Yup.string()
        .min(3, t('service-provider-app.connect-account.form.valid.zip-code'))
        .max(9, t('service-provider-app.connect-account.form.valid.zip-code'))
        .required(
          t('service-provider-app.connect-account.form.required.zip-code')
        ),
      city: Yup.string()
        .min(2, t('service-provider-app.connect-account.form.valid.city'))
        .max(255, t('service-provider-app.connect-account.form.valid.city'))
        .required(t('service-provider-app.connect-account.form.required.city')),
      countryId: Yup.string().required(
        t('service-provider-app.connect-account.form.required.country-id')
      ),
      phone: Yup.string()
        .matches(PHONE_NUMBER_REGEX, {
          message: t('service-provider-app.connect-account.form.valid.phone'),
        })
        .required(
          t('service-provider-app.connect-account.form.required.phone')
        ),
    }),
    [t]
  );
};
